import Menu from "../components/general/Menu";
import AppLoader from 'components/general/AppLoader';
import Footer from "../components/general/Footer";

import { createContext, useEffect, useRef, useState, useCallback } from "react";

import { Row, Col, Container, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';

import MeetingCard from "components/Meeting/MeetingCard";
import AgendaComponent from "components/Meeting/AgendaComponent";
import AgendaToolbar from "components/Meeting/AgendaToolbar";
import ConfigToolbar from "components/Meeting/ConfigToolbar";
import { useRemoteDisplayRegistration } from 'components/Meeting/RemoteDisplayRegistration';
import AgendaItemModal from "components/Meeting/modals/AgendaItemModal";
import ContestJudgeModal from "components/Meeting/ContestJudgeModal";
import ChiefJudgeModal from "components/Meeting/ChiefJudgeModal";
import { getSocket } from '../socket';
import useMeetingAgendaUpdates from '../components/Meeting/useMeetingAgendaUpdates';
import { useCompactPortrait, usePageNavigation } from 'components/navigation/NavigationContext';
import useTableTopics from 'components/Meeting/tableTopics/useTableTopics';
import TableTopicsResultsModal from 'components/Meeting/tableTopics/TableTopicsResultsModal';

export const MeetingContext = createContext();

export default function Meeting() {
  // Toolbar state and logic (must be inside component)
  const { user: authUser } = useSelector((x) => x.auth);
  const [isNoSleep, setIsNoSleep] = useState(false);
  const [toggleText, setToggleText] = useState('Keep Screen On');
  const [toggleColor, setToggleColor] = useState('primary');
  const [editMode, setEditMode] = useState(false);
  const [showClockText, setShowClockText] = useState(true);

  const toggleWakeOnScreen = (e) => {
    if (e) e.preventDefault();
    setIsNoSleep((prev) => {
      const next = !prev;
      setToggleText(next ? 'Allow screen off' : 'Keep Screen On');
      setToggleColor(next ? 'danger' : 'primary');
      if (!next && toggleColor === 'danger') { window.location.reload(false); }
      return next;
    });
  };
  const params = useParams();
  const url = process.env.REACT_APP_URL;
  const meetingId = params.meetingId;
  const meetingLoadSequence = useRef(0);

  useEffect(() => {
    if (!meetingId) return undefined;
    const socket = getSocket();
    const handleClockVisibility = (payload) => {
      if (String(payload?.meetingId || '') !== String(meetingId) || typeof payload?.visible !== 'boolean') return;
      setShowClockText(payload.visible);
    };
    socket.on('meeting:clock-visibility', handleClockVisibility);
    socket.emit('meeting:join', String(meetingId));
    return () => socket.off('meeting:clock-visibility', handleClockVisibility);
  }, [meetingId]);

  const [meeting, setMeeting] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [agenda, setAgenda] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [hasPrevMeeting, setHasPrevMeeting] = useState(false);
  const [hasNextMeeting, setHasNextMeeting] = useState(false);
  const [prevMeetingId, setPrevMeetingId] = useState('');
  const [nextMeetingId, setNextMeetingId] = useState('');
  const [isAssignedJudge, setIsAssignedJudge] = useState(false);
  const [isAssignedTiebreaker, setIsAssignedTiebreaker] = useState(false);
  const [isAssignedChiefJudge, setIsAssignedChiefJudge] = useState(false);
  const [showJudgeModal, setShowJudgeModal] = useState(false);
  const [showTiebreakerModal, setShowTiebreakerModal] = useState(false);
  const [showChiefJudgeModal, setShowChiefJudgeModal] = useState(false);
  const [showTableTopicsResults, setShowTableTopicsResults] = useState(false);
  const [tableTopicsResultsParent, setTableTopicsResultsParent] = useState('tools');
  const [tableTopicsResultsPageId, setTableTopicsResultsPageId] = useState(null);
  const [remoteDisplay, setRemoteDisplay] = useState(null);
  const remoteDisplayRegistration = useRemoteDisplayRegistration(meetingId);
  const compact = useCompactPortrait();
  const tableTopics = useTableTopics({ meetingId, authUser });

  useEffect(() => {
    if (!remoteDisplay) return undefined;
    document.body.classList.add('display-runtime-active');
    return () => document.body.classList.remove('display-runtime-active');
  }, [remoteDisplay]);

  useEffect(() => {
    if (!meetingId) return undefined;
    const socket = getSocket();
    const handleAgendaItemVisibility = (payload) => {
      if (String(payload?.meetingId || '') !== String(meetingId) || !payload?.agendaItemId) return;
      const agendaItemId = String(payload.agendaItemId);
      const hideTitleAndSubtitle = payload.hideTitleAndSubtitle === true;
      setAgenda((current) => current.map((agendaItem) => (
        String(agendaItem?.agendaItemId || '') === agendaItemId
          ? { ...agendaItem, hideTitleAndSubtitle }
          : agendaItem
      )));
      setMeeting((current) => ({
        ...current,
        items: (Array.isArray(current?.items) ? current.items : []).map((agendaItem) => (
          String(agendaItem?.agendaItemId || '') === agendaItemId
            ? { ...agendaItem, hideTitleAndSubtitle }
            : agendaItem
        )),
      }));
    };
    const handleRemoteDisplayCommand = (payload) => {
      if (String(payload?.meetingId || '') !== String(meetingId)) return;
      setRemoteDisplay(payload?.agendaItemId ? {
        agendaItemId: String(payload.agendaItemId),
        showTimer: payload.showTimer !== false,
        hideTitleAndSubtitle: payload.hideTitleAndSubtitle === true,
      } : null);
    };
    socket.on('meeting:agenda-item-visibility', handleAgendaItemVisibility);
    socket.on('meeting:remote-display-command', handleRemoteDisplayCommand);
    return () => {
      socket.off('meeting:agenda-item-visibility', handleAgendaItemVisibility);
      socket.off('meeting:remote-display-command', handleRemoteDisplayCommand);
    };
  }, [meetingId]);
  const loadMeeting = useCallback(async (view = '') => {
    const sequence = ++meetingLoadSequence.current;
    try {
      const query = view ? `?view=${encodeURIComponent(view)}` : '';
      const response = await fetch(`${url}/meeting/${meetingId}${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (sequence !== meetingLoadSequence.current) return false;
        setMeeting(data);
        setShowClockText(data?.meetingDetails?.showClockText !== false);
        setAgenda(data.items);
        if (Array.isArray(data.roleAssignments)) {
          setAssignments(data.roleAssignments);
        }
        return true;
      } else {
        const errorData = await response.json();
        toast.error(`Unable to load the meeting. Error: ${errorData.Error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading meeting: ${error.message}`, { position: 'bottom-left' });
    }
    return false;
  }, [url, meetingId, authUser?.token]);

  const getMeeting = useCallback(() => loadMeeting(), [loadMeeting]);
  const getCoreMeeting = useCallback(() => loadMeeting('core'), [loadMeeting]);

  useMeetingAgendaUpdates(meetingId, getMeeting);

  const getRoleAssignment = useCallback(async () => {
    try {
      const response = await fetch(`${url}/roleAssignments/${meetingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (response.ok) {
        const roleAssignment = await response.json();
        setAssignments(roleAssignment);
      } else {
        const errorData = await response.json();
        toast.error(`Unable to load role assignments. Error: ${errorData.Error || response.statusText}`, { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Error loading role assignments: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meetingId, authUser?.token]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      const coreLoaded = await getCoreMeeting();
      if (cancelled) return;
      setIsLoading(false);

      if (coreLoaded) {
        void getMeeting();
      } else {
        await Promise.all([getMeeting(), getRoleAssignment()]);
      }
    };

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [getCoreMeeting, getMeeting, getRoleAssignment]);

  const getPreviousMeeting = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meetings/previous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: meeting?.meetingDetails?.meetingDate }),
      });
      const data = await response.json();
      if (data && data.Message === 'No previous meetings found') {
        setHasPrevMeeting(false); // Explicitly set to false if no previous meeting is found
      } else {

        setPrevMeetingId(data._id);
        setHasPrevMeeting(true);
      }
    } catch (error) {
      toast.error(`Error loading previous meeting: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meeting?.meetingDetails?.meetingDate]);

  const getNextMeeting = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meetings/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: meeting?.meetingDetails?.meetingDate }),
      });
      const data = await response.json();
      if (data && data.Message === 'No next meetings found') {
        setHasNextMeeting(false); // Explicitly set to false if no next meeting is found
      } else {
        setNextMeetingId(data._id);
        setHasNextMeeting(true);
      }
    } catch (error) {
      toast.error(`Error loading next meeting: ${error.message}`, { position: 'bottom-left' });
    }
  }, [url, meeting?.meetingDetails?.meetingDate]);

  useEffect(() => {
    if (meeting.meetingDetails) {
      getPreviousMeeting();
      getNextMeeting();
    }
  }, [meeting, getPreviousMeeting, getNextMeeting]);

  const refreshContestAssignments = useCallback(() => {
    if (!meeting?.meetingDetails?.isContest || !meetingId || !authUser?.token) {
      setIsAssignedJudge(false);
      setIsAssignedTiebreaker(false);
      setIsAssignedChiefJudge(false);
      return Promise.resolve();
    }

    const authIdentityIds = new Set([
      String(authUser?.user?._id || ''),
      String(authUser?.user?.id || ''),
      String(authUser?.user?.tmAuthUserId || ''),
      String(authUser?.user?.localUserId || ''),
      String(authUser?._id || ''),
      String(authUser?.id || ''),
      String(authUser?.tmAuthUserId || ''),
    ].map((value) => value.trim()).filter(Boolean));

    return fetch(`${url}/contesthub/meetings/${meetingId}/setup`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authUser.token}`,
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const contestJudges = Array.isArray(data?.contestJudges) ? data.contestJudges : [];
        const assignedJudgeIds = contestJudges.flatMap((entry) => (
          Array.isArray(entry?.judges) ? entry.judges : []
        ));

        setIsAssignedJudge(assignedJudgeIds.some((judgeId) => authIdentityIds.has(String(judgeId || '').trim())));
        setIsAssignedTiebreaker(contestJudges.some((entry) => authIdentityIds.has(String(entry?.tiebreakingJudge || '').trim())));
        setIsAssignedChiefJudge(contestJudges.some((entry) => authIdentityIds.has(String(entry?.chiefJudge || '').trim())));
      })
      .catch(() => {
        setIsAssignedJudge(false);
        setIsAssignedTiebreaker(false);
        setIsAssignedChiefJudge(false);
      });
  }, [authUser, meeting?.meetingDetails?.isContest, meetingId, url]);

  useEffect(() => {
    void refreshContestAssignments();
  }, [refreshContestAssignments]);

  useEffect(() => {
    if (!meetingId) return undefined;
    const socket = getSocket();
    const handleContestSetupUpdated = (payload) => {
      if (String(payload?.meetingId || '') === String(meetingId)) {
        void refreshContestAssignments();
      }
    };

    socket.on('contesthub:setup-updated', handleContestSetupUpdated);
    return () => socket.off('contesthub:setup-updated', handleContestSetupUpdated);
  }, [meetingId, refreshContestAssignments]);

  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked);
  const remoteAgendaItem = remoteDisplay
    ? agenda.find((item) => String(item?.agendaItemId || '') === remoteDisplay.agendaItemId)
    : null;
  const displayedRemoteAgendaItem = remoteAgendaItem && remoteDisplay
    ? { ...remoteAgendaItem, hideTitleAndSubtitle: remoteDisplay.hideTitleAndSubtitle }
    : null;
  usePageNavigation({
    preferredOrientation: 'portrait',
    primaryAction: null,
  });

  useEffect(() => {
    if (isMeetingLocked) setEditMode(false);
  }, [isMeetingLocked]);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <AppLoader label="Loading..." />
      </div>
    );
  }

  return (
    <section className="main meeting-page">
      <Menu />
      <div className="meeting-navigation-toolbar-fixed">
        <div className="agenda-toolbar-icons" style={{ marginTop: '-120px' }}>
          {hasPrevMeeting && (
            <Button variant="success" href={`/meeting/${prevMeetingId}`}>
              <i className="bi bi-chevron-left"></i>
            </Button>
          )}
        </div>
      </div>

      <MeetingCard
        meeting={meeting}
        getMeeting={getMeeting}
        assignments={assignments}
        getRoleAssignment={getRoleAssignment}
        isLocked={isMeetingLocked}
      />
      <AgendaToolbar
        toggleText={toggleText}
        toggleColor={toggleColor}
        isNoSleep={isNoSleep}
        toggleWakeOnScreen={toggleWakeOnScreen}
        editMode={editMode}
        setEditMode={setEditMode}
        getMeeting={getMeeting}
        isLocked={isMeetingLocked}
        isAssignedJudge={isAssignedJudge}
        onOpenJudgePanel={() => setShowJudgeModal(true)}
        isAssignedTiebreaker={isAssignedTiebreaker}
        onOpenTiebreakerPanel={() => setShowTiebreakerModal(true)}
        isAssignedChiefJudge={isAssignedChiefJudge}
        onOpenChiefJudgePanel={() => setShowChiefJudgeModal(true)}
        onOpenTableTopicsResults={tableTopics.reviews?.length ? (parent = 'tools', pageId = null) => {
          setTableTopicsResultsParent('tools');
          setTableTopicsResultsPageId(pageId);
          setShowTableTopicsResults(true);
        } : null}
      />
      <div className="config-toolbar-fixed">
        <div className="config-toolbar-icons" style={{ marginTop: '-120px' }}>
          {hasNextMeeting && (
            <Button variant="success" href={`/meeting/${nextMeetingId}`}>
              <i className="bi bi-chevron-right"></i>
            </Button>
          )}
        </div>
      </div>

      <Container className="meeting-agenda-container">

        <div
          className="meeting-agenda-scroll"
          style={{
            maxHeight: "calc(100dvh - var(--meeting-scroll-buffer, 16rem))",
            overflowY: "auto",
            overflowX: "hidden", // Prevent horizontal scroll
            paddingBottom: "4rem",
            boxSizing: "border-box",
            scrollPaddingBottom: "4rem",
          }}
        >
          <Row className="meeting-agenda-root-row">
            <Col xs={12} className="meeting-agenda-root-col">
              <AgendaComponent
                getMeeting={getMeeting}
                meeting={meeting}
                editMode={editMode}
                showClockText={showClockText}
                assignments={assignments}
                tableTopics={tableTopics}
                onOpenTableTopicsResults={(parent, pageId = null) => {
                  setTableTopicsResultsParent(parent || 'tools');
                  setTableTopicsResultsPageId(pageId);
                  setShowTableTopicsResults(true);
                }}
              />
            </Col>
          </Row>
        </div>
      </Container>
      <Footer />
      <ConfigToolbar
        getMeeting={getMeeting}
        getRoleAssignment={getRoleAssignment}
        assignments={assignments}
        agenda={agenda}
        meeting={meeting}
        isLocked={isMeetingLocked}
        remoteDisplayRegistration={remoteDisplayRegistration}
      />
      <ContestJudgeModal
        meeting={meeting}
        show={showJudgeModal}
        onClose={() => setShowJudgeModal(false)}
      />
      <ContestJudgeModal
        meeting={meeting}
        show={showTiebreakerModal}
        onClose={() => setShowTiebreakerModal(false)}
        panelMode="tiebreaker"
      />
      <ChiefJudgeModal
        meeting={meeting}
        show={showChiefJudgeModal}
        onClose={() => setShowChiefJudgeModal(false)}
      />
      <TableTopicsResultsModal
        show={showTableTopicsResults && Boolean(tableTopics.reviews?.length)}
        onHide={() => setShowTableTopicsResults(false)}
        tableTopics={tableTopics}
        items={meeting?.items}
        navigationParent={compact ? tableTopicsResultsParent : null}
        agendaItemId={tableTopicsResultsPageId}
      />
      {displayedRemoteAgendaItem && (
        <AgendaItemModal
          key={displayedRemoteAgendaItem.agendaItemId}
          agenda={agenda}
          item={displayedRemoteAgendaItem}
          getMeeting={getMeeting}
          meeting={meeting}
          showModal
          handleCloseModal={() => setRemoteDisplay(null)}
          showClockText={showClockText}
          timerDisplayEnabled={remoteDisplay.showTimer}
          remoteControlled
        />
      )}
    </section>
  );
}